/**
 * 新消息实时提醒：系统通知 + 轻提示音。
 *  - 纯工具层，不依赖 React；由 ChatService 收信时调用。
 *  - 幂等初始化：AudioContext 延迟创建；提示音限频避免连续轰炸。
 */

let audioCtx: AudioContext | null = null;
let lastToneAt = 0;
const TONE_MIN_GAP_MS = 2_000;

/** 请求系统通知权限（仅在用户收到第一条消息时触发一次）。 */
export function ensureNotifyPermission(): void {
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      void Notification.requestPermission().catch(() => undefined);
    }
  } catch {
    /* 静默：无通知权限不影响核心功能 */
  }
}

/** WebAudio 双音提示（880Hz → 1320Hz，各 120ms）。失败静默。 */
function playTone(): void {
  try {
    const now = Date.now();
    if (now - lastToneAt < TONE_MIN_GAP_MS) return;
    lastToneAt = now;
    if (typeof window === 'undefined') return;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    if (!audioCtx) audioCtx = new Ctor();
    if (audioCtx.state === 'suspended') void audioCtx.resume().catch(() => undefined);
    const beep = (freq: number, start: number, dur: number): void => {
      const osc = audioCtx!.createOscillator();
      const gain = audioCtx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, audioCtx!.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.08, audioCtx!.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx!.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(audioCtx!.destination);
      osc.start(audioCtx!.currentTime + start);
      osc.stop(audioCtx!.currentTime + start + dur + 0.02);
    };
    beep(880, 0, 0.12);
    beep(1320, 0.14, 0.12);
  } catch {
    /* 提示音失败不影响消息收发 */
  }
}

/**
 * 新消息提醒入口（收信时调用）。
 * @param name 对方昵称（或 peerId 短显）
 * @param body 消息正文（截断）
 * @param opts.muted 会话静音（不提醒）
 */
export function notifyIncoming(name: string, body: string, opts: { muted?: boolean } = {}): void {
  if (opts.muted) return;
  playTone();
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      const n = new Notification(`💬 ${name}`, {
        body: body.length > 120 ? `${body.slice(0, 120)}…` : body,
        tag: 'mesh-incoming',
        silent: true, // 声音由 WebAudio 统一控制
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } else if (Notification.permission === 'default') {
      ensureNotifyPermission();
    }
  } catch {
    /* 通知 API 异常静默 */
  }
}
